import { useEffect, useMemo, useRef, useState } from 'react';
import { Send, Smile } from 'lucide-react';
import clsx from 'clsx';
import type { Person } from '@/utils/commsHelpers';

interface Props {
  onSend: (body: string, mentions: string[]) => void;
  people: Person[];
  placeholder?: string;
  testid?: string;
  autoFocus?: boolean;
}

const QUICK_EMOJI = ['👍', '🙌', '🔥', '✅', '❌', '😂', '❤️', '💀', '🤝', '💰', '⚡', '👀'];

interface MentionDetect {
  active: boolean;
  start: number;
  query: string;
}

function detectMention(body: string, caret: number): MentionDetect {
  // Look backward from caret to find an "@" not preceded by a word char.
  let i = caret - 1;
  while (i >= 0) {
    const ch = body[i]!;
    if (ch === '@') {
      const prev = i > 0 ? body[i - 1]! : ' ';
      if (/\s|^/.test(prev) || i === 0) {
        return { active: true, start: i, query: body.slice(i + 1, caret) };
      }
      return { active: false, start: -1, query: '' };
    }
    if (/\s/.test(ch)) break;
    i--;
  }
  return { active: false, start: -1, query: '' };
}

export default function MessageComposer({
  onSend,
  people,
  placeholder = 'Message…',
  testid = 'comms-composer',
  autoFocus,
}: Props) {
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState<string[]>([]);
  const [showEmoji, setShowEmoji] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const [caretPos, setCaretPos] = useState(0);

  const mention = useMemo(() => detectMention(body, caretPos), [body, caretPos]);
  const matches = useMemo(() => {
    if (!mention.active) return [];
    const q = mention.query.toLowerCase();
    return people
      .filter((p) => p.id !== 'system-bot' && p.id !== 'system-ai-voice')
      .filter((p) => !q || p.displayName.toLowerCase().includes(q) || p.id.toLowerCase().includes(q))
      .slice(0, 6);
  }, [mention, people]);

  useEffect(() => {
    if (autoFocus) taRef.current?.focus();
  }, [autoFocus]);

  const send = () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    onSend(trimmed, mentions);
    setBody('');
    setMentions([]);
    setShowEmoji(false);
  };

  const insertMention = (person: Person) => {
    if (!mention.active) return;
    const before = body.slice(0, mention.start);
    const after = body.slice(caretPos);
    const tag = `@${person.id}`;
    const next = `${before}${tag} ${after}`;
    setBody(next);
    setMentions((m) => (m.includes(person.id) ? m : [...m, person.id]));
    requestAnimationFrame(() => {
      const pos = before.length + tag.length + 1;
      taRef.current?.setSelectionRange(pos, pos);
      taRef.current?.focus();
      setCaretPos(pos);
    });
  };

  const insertEmoji = (e: string) => {
    const before = body.slice(0, caretPos);
    const after = body.slice(caretPos);
    setBody(`${before}${e}${after}`);
    setShowEmoji(false);
    requestAnimationFrame(() => taRef.current?.focus());
  };

  return (
    <div className="relative" data-testid={testid}>
      {mention.active && matches.length > 0 ? (
        <div
          className="absolute bottom-full left-0 mb-1 panel py-1 w-72 shadow-ink-soft z-10"
          data-testid="comms-mention-list"
        >
          {matches.map((p) => (
            <button
              key={p.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                insertMention(p);
              }}
              className="w-full px-3 py-1.5 text-left flex items-center gap-2 hover:bg-ink-soft/60"
              data-testid={`comms-mention-${p.id}`}
            >
              <span className="font-inter text-sm text-cream">{p.displayName}</span>
              <span className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">
                {p.role}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {showEmoji ? (
        <div
          className="absolute bottom-full right-0 mb-1 panel py-2 px-3 flex gap-1.5 z-10 shadow-ink-soft"
          data-testid="comms-emoji-picker"
        >
          {QUICK_EMOJI.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => insertEmoji(e)}
              className="text-lg hover:scale-125 transition"
            >
              {e}
            </button>
          ))}
        </div>
      ) : null}

      <div className="flex gap-2 items-end p-3 border-t border-gold-dim/15 bg-ink-deep">
        <textarea
          ref={taRef}
          value={body}
          rows={1}
          onChange={(e) => {
            setBody(e.target.value);
            setCaretPos(e.target.selectionStart ?? e.target.value.length);
          }}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              send();
            }
            requestAnimationFrame(() => setCaretPos(taRef.current?.selectionStart ?? 0));
          }}
          onClick={() => setCaretPos(taRef.current?.selectionStart ?? 0)}
          placeholder={placeholder}
          className={clsx(
            'flex-1 bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream font-serif resize-none',
            'focus:outline-none focus:border-gold-bright',
            'min-h-[40px] max-h-[160px]',
          )}
          data-testid="comms-composer-input"
        />
        <button
          type="button"
          onClick={() => setShowEmoji((v) => !v)}
          className="btn-quiet p-2"
          aria-label="Emoji"
          data-testid="comms-emoji-toggle"
        >
          <Smile size={14} />
        </button>
        <button
          type="button"
          onClick={send}
          disabled={!body.trim()}
          className="btn-primary text-xs"
          data-testid="comms-send"
          title="Send (⌘⏎)"
        >
          <Send size={12} /> Send
        </button>
      </div>
    </div>
  );
}
